/**
 * components/ErrorBoundary.tsx — the floor beneath a failure.
 *
 * `05-client.md` §5.1 is one requirement with a long reason attached, and the reason is what makes this
 * file more than tidiness: **an interactive app MUST catch rendering and runtime errors at a boundary and
 * present a recoverable state.** In a component-tree framework an uncaught error unmounts the tree and
 * the result is a white page — "the single worst failure state an application has, because it is
 * indistinguishable from a network failure, a bad deploy, an expired session and a bug, and it tells the
 * user nothing about which. It is also the one they will not report, because there is nothing to
 * describe."
 *
 * Until 7 August 2026 this application had no boundary anywhere: no `componentDidCatch`, no
 * `getDerivedStateFromError`, nothing. Every screen's own four states were correct and complete, and all
 * four were irrelevant the moment a render threw.
 *
 * ## A class, in a tier of function components
 *
 * React offers no hook for this. `getDerivedStateFromError` and `componentDidCatch` exist only on class
 * components and they are the whole mechanism; the alternative is a dependency, and this application's
 * entire web dependency list is react and react-dom.
 *
 * ## Two of them, because one is not enough — in either direction
 *
 * §5.1's SHOULD: the boundary "SHOULD be placed so that a failure in one region does not take the whole
 * application with it. An error in a chart should not remove the navigation the user needs to get away
 * from it."
 *
 *   - **Outermost only** (`main.tsx`) catches everything and answers everything the same way: a run
 *     history that throws takes the rail, the tab bar, the unit switcher and the account menu with it,
 *     and the reader's only way off the page is the browser's own Back or reload — the precise recourse
 *     §5.1 says recovery must not require.
 *   - **Inner only** (`App.tsx`, inside the shell) leaves the shell itself unguarded, and the shell is
 *     not a safe assumption: it reads `me`, draws the switcher from `accessibleScopes` and the account
 *     menu from the session. A boundary that cannot catch the component containing it has a hole exactly
 *     where the whole page lives.
 *
 * So the inner one wraps the routed page body and the outer one wraps `App`. Same component, different
 * copy, because the only difference the reader needs is what is still usable.
 *
 * ## The reference, and why it is minted where it is
 *
 * §5.2: the user-facing message MUST NOT contain a stack trace, an internal identifier, a database error,
 * a file path or an exception class name — it is useless to the person reading it and it is
 * reconnaissance for everybody else. "A correlation identifier MUST be shown instead — displayed,
 * selectable, and carried in the report the app sends to its own telemetry — so that the detail exists in
 * one place, where it is useful, and the user's copy is a key to it rather than a copy of it."
 *
 * It is minted in `componentDidCatch`, **at catch time and never at render time**. A reference computed
 * during render changes on every re-render, so the string the reader is halfway through reading aloud
 * stops matching the one in the log, and one failure acquires a fresh key per paint.
 *
 * `crypto.randomUUID` is feature-detected rather than called, and the fallback is load-bearing rather
 * than superstition: it is defined only in a **secure context**, so an internal deployment reached over
 * plain HTTP at a LAN address — the most ordinary staging arrangement there is — has `crypto` and no
 * `randomUUID`. An unguarded call would then throw *inside the error handler*, React unmounts the tree
 * when a boundary's own lifecycle throws, and the user gets the white page anyway, from the component
 * whose entire purpose was to prevent it. The fallback does not need to be unguessable; it is a lookup
 * key in a log, not a secret.
 *
 * Where the detail actually goes is `console.error`, keyed by that reference, with the error and React's
 * component stack. ticketless has no browser telemetry sink today, so the console is the whole of the
 * report the app sends; when a sink is added it is this one call that grows a destination, and nothing
 * on the screen changes.
 *
 * ## Getting out, and the control that would otherwise do nothing
 *
 * Recovery "MUST NOT require the user to know to press the browser's own reload", so there are two ways
 * out and both are on screen: **Try again**, which clears the boundary and re-renders the same route, and
 * a link to the application root.
 *
 * The link is the subtle one. Clearing the boundary is not sufficient on its own, and neither is
 * navigating:
 *
 *   - The inner boundary sits *below* `App`, which holds the route in state and updates it on
 *     `hashchange`. Clearing synchronously would therefore re-render the route that has just thrown.
 *   - A crash left standing while the surviving navigation is used would make the failure permanent —
 *     the rail is the entire point of the inner boundary, and every item in it is an `<a href="#/…">`
 *     that changes nothing above this component.
 *
 * So the boundary listens for `hashchange` and clears itself on any navigation, which makes the rail, the
 * tab bar, the account menu, the browser's Back button and this link all work without any of them knowing
 * it exists. The one case that fires no event is a link to the address the window is already at — and a
 * control that does nothing is what §5.5 forbids outright — so that single case, and only that one, is
 * handled in the click.
 */
import { Component, type ErrorInfo, type MouseEvent, type ReactNode } from "react";
import { Button } from "./ui";

/**
 * The application's root.
 *
 * `parseRoute` keeps the empty hash and `#/` on the job list deliberately, and `Shell`'s brand links to
 * the same address. It is the one address in this application guaranteed to resolve, which is what
 * qualifies it as the way back from a screen that has just failed.
 */
const ROOT_HASH = "#/";

/** Which of the two boundaries this is. It selects the copy and nothing else. */
export type CrashRegion = "page" | "application";

interface CrashCopy {
  readonly title: string;
  readonly body: string;
}

/**
 * What happened, why so far as it is knowable, and what to do next — `05-content.md` §4's three
 * questions, which most machine-written messages answer none of.
 *
 * "Why" is deliberately not invented: nobody knows why, which is what an uncaught exception means. What
 * *is* knowable is what it was not — nothing saved, nothing signed out — and that is worth saying,
 * because those are the two conclusions a reader draws from a screen that stops, and both send them off
 * to redo work that was never lost.
 */
const COPY: Readonly<Record<CrashRegion, CrashCopy>> = {
  page: {
    title: "This page stopped before it finished drawing",
    body:
      "Something inside it failed, so it stopped rather than showing you half a page. Nothing you were " +
      "looking at has been changed, and you have not been signed out. Try again, or use the navigation " +
      "to go somewhere else — the rest of the application is still working.",
  },
  application: {
    title: "ticketless stopped before it finished drawing",
    body:
      "Something failed while the application was being drawn, so it stopped rather than leaving you a " +
      "blank page. Nothing you were looking at has been changed, and you have not been signed out. Try " +
      "again, or go back to the start.",
  },
};

/**
 * A key for the log, minted once per caught error.
 *
 * The cast is the point of the line: `lib.dom` types `globalThis.crypto` as always present and every
 * browser agrees, but `randomUUID` on it is secure-context-only. See the header — an unguarded call is
 * a throw inside the error handler, which is the one failure this component cannot survive.
 */
function newReference(): string {
  const api = globalThis.crypto as Crypto | undefined;
  if (api !== undefined && typeof api.randomUUID === "function") return api.randomUUID();
  return `ref-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export interface ErrorBoundaryProps {
  readonly region: CrashRegion;
  readonly children: ReactNode;
}

interface ErrorBoundaryState {
  /**
   * Set by `getDerivedStateFromError` in the render phase; `reference` arrives one commit later from
   * `componentDidCatch`. The two cannot be one field: between them there is a render in which the
   * boundary has failed and has no reference yet, and the screen has to be correct in that frame too.
   */
  readonly failed: boolean;
  readonly reference: string | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { failed: false, reference: null };
  }

  /**
   * The render-phase half: show the fallback.
   *
   * The error itself is deliberately NOT put into state. Nothing here may render it (§5.2), and state is
   * precisely where a later edit would find it lying ready to be printed "just while we debug this".
   */
  static getDerivedStateFromError(): Partial<ErrorBoundaryState> {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    const reference = newReference();
    // The commit-phase half, once per caught error. Everything §5.2 keeps off the screen goes here
    // instead, keyed by the one string the user can read out.
    console.error(`ticketless render failure [${reference}]`, error, info.componentStack ?? "");
    this.setState({ reference });
  }

  override componentDidMount(): void {
    // Any navigation clears the boundary — see the header. Attached unconditionally rather than only
    // while failed: two listeners' worth of bookkeeping to save one `addEventListener` is how a
    // recovery path acquires a state machine.
    window.addEventListener("hashchange", this.reset);
  }

  override componentWillUnmount(): void {
    window.removeEventListener("hashchange", this.reset);
  }

  /** Idempotent: three things call it, and two of them can arrive with nothing to clear. */
  private readonly reset = (): void => {
    if (!this.state.failed && this.state.reference === null) return;
    this.setState({ failed: false, reference: null });
  };

  private readonly onLeave = (event: MouseEvent<HTMLAnchorElement>): void => {
    // Modified clicks belong to the browser: ⌘-click and middle-click open a new tab, and a boundary
    // that swallowed them would be a worse control than the one it is here to fix.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    // A link to the address the window is ALREADY at fires no `hashchange`, so nothing would clear the
    // boundary and this control would do nothing whatever. Every other case is left entirely to the
    // browser and answered by the listener, by which time `App` has re-read the route.
    if (window.location.hash === ROOT_HASH) this.reset();
  };

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;

    const copy = COPY[this.props.region];
    const { reference } = this.state;

    return (
      <div className="crash" role="alert">
        <h1 className="crash__title">{copy.title}</h1>
        <p className="crash__body">{copy.body}</p>
        {/* Absent for the single frame between the two halves of the catch, rather than shown as a
            placeholder: a reference that says "generating…" is one somebody reads out. */}
        {reference === null ? null : (
          <p className="crash__body">
            Quote this reference if you report it: <code className="crash__ref">{reference}</code>
          </p>
        )}
        <div className="crash__actions">
          <Button variant="primary" onClick={this.reset}>
            Try again
          </Button>
          {/* A hand-written anchor rather than `ButtonLink`, which takes no `onClick` — see `onLeave`,
              which is what stops this being a dead control at the root address. It wears the kit's own
              classes, so the size, the states and the coarse-pointer 44px floor are unchanged. */}
          <a className="btn btn--secondary" href={ROOT_HASH} onClick={this.onLeave}>
            <span className="btn__label">Go to the start</span>
          </a>
        </div>
      </div>
    );
  }
}
